import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Hotel, Search, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { usePilgrimGuideStore } from '../stores/pilgrimGuideStore';
import { usePackagesStore } from '../stores/packagesStore';
import { useAccommodationStore } from '../stores/accommodationStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { TasksFilters } from '../components/accommodationTasks/TasksFilters';
import { AccommodationTasksList } from '../components/accommodationTasks/AccommodationTasksList';
import { CountryAccommodationChart } from '../components/accommodationTasks/CountryAccommodationChart';
import { useLanguage } from '../contexts/LanguageContext';
import { LoadingProgress } from '../components/common/LoadingProgress';

export const AccommodationTasksPage = () => {
  const { translations } = useLanguage();
  const { 
    links, 
    loading: linksLoading, 
    initialized,
    progress,
    fetchLinks 
  } = usePilgrimGuideStore();
  const { packages, loading: packagesLoading, error: packagesError, fetchPackages } = usePackagesStore();
  const { tables, loading: tablesLoading, error: tablesError, fetchTables } = useAccommodationStore();

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGender, setSelectedGender] = useState<'all' | 'male' | 'female'>('all');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'completed' | 'pending' | 'partial'>('all');
  const [selectedHotel, setSelectedHotel] = useState('');
  const [showChart, setShowChart] = useState(true);

  // Create a package map keyed by name for easier lookup
  const packagesByName = React.useMemo(() => {
    const map: Record<string, any> = {};
    Object.values(packages).forEach(pkg => {
      if (pkg.name) {
        map[pkg.name] = pkg;
      }
    });
    return map;
  }, [packages]);
  const visibleLinks = React.useMemo(
    () => Object.values(links).filter(link => link.status !== 'waiting_identification'),
    [links]
  );

  // Initialize data
  useEffect(() => {
    if (!initialized) {
      fetchLinks();
    }
    fetchPackages();
    fetchTables();
  }, [initialized, fetchLinks, fetchPackages, fetchTables]);

  if ((linksLoading && !initialized) || packagesLoading || tablesLoading) {
    return <LoadingSpinner />;
  }

  if (packagesError || tablesError) {
    return <ErrorMessage message={packagesError || tablesError} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Hotel className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {translations?.accommodationTasks?.header || 'Accommodation Tasks'}
            </h1>
            <p className="text-gray-500 mt-1">
              {translations?.accommodationTasks?.subheader ||
                'Monitor the accommodation status of pilgrims in hotels'}
            </p>
          </div>
        </div>
        
        {/* Toggle chart button */}
        <button
          onClick={() => setShowChart(!showChart)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
        >
          {showChart ? (
            <>
              <ChevronUp size={18} />
              <span>Hide Country Chart</span>
            </>
          ) : (
            <>
              <ChevronDown size={18} />
              <span>Show Country Chart</span>
            </>
          )}
        </button>
      </motion.div>

      {/* Loading Progress */}
      {linksLoading && !initialized && progress.total > 0 && (
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <LoadingProgress
            progress={progress.current}
            total={progress.total}
            label={translations?.accommodationTasks?.loadingLinks || 'Loading links...'}
          />
        </div>
      )}
      
      {/* Country Accommodation Chart */}
      {showChart && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ delay: 0.05 }}
        >
          <CountryAccommodationChart 
            links={visibleLinks}
            packagesMap={packagesByName}
          />
        </motion.div>
      )}

      {/* Filters Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border border-gray-100"
      >
        <TasksFilters
          searchTerm={searchTerm}
          selectedGender={selectedGender}
          selectedStatus={selectedStatus}
          selectedHotel={selectedHotel}
          onSearchChange={setSearchTerm}
          onGenderChange={setSelectedGender}
          onStatusChange={setSelectedStatus}
          onHotelChange={setSelectedHotel}
        />
      </motion.div>

      {/* Tasks List */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        {visibleLinks.length > 0 ? (
          <AccommodationTasksList
            links={visibleLinks}
            packagesMap={packagesByName}
          />
        ) : (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-500">
            {translations?.accommodationTasks?.noLinks || 'No pilgrims found. Please link pilgrims first.'}
          </div>
        )}
      </motion.div>
    </div>
  );
};
